import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card, DangerZone, ProgressBar, StatCard } from '../../ui';
import { ProcessorStatePanel, useProcessorStates } from '../../ProcessorStateBadge';
import { useAiSwitch } from '../../curation/automation/useAiSwitch';
import { AutomationSwitchNotice, switchMoved } from '../AutomationSwitchNotice';
import { formatWhen } from '../../curation/format';

const STATUS_URL = '/ui/admin/processors/caf-backlog/status';
const HAIKU_STATUS_URL = '/ui/admin/processors/caf-backlog/haiku-backfill/status';
const CONFIG_URL = '/ui/admin/processor-state/caf-provider-config';
interface CafProviderConfig {
  provider: string; model: string; global_concurrency: number; max_retries: number; config_version: number;
}

const HAIKU_CONFIRM_TEXT =
  'Run eligible backfill tasks through the configured provider? ' +
  'The shared concurrency limit applies across both lanes and all instances. ' +
  'Provider charges depend on actual usage. You can request stop at any time.';

interface ClawbackResults {
  tasksScanned: number;
  duplicateClaimsFound: number;
  tasksReset: number;
  dryRun: boolean;
  sampleMatches: Array<{ id: string; claimRef: string; emailSubject: string; emailFrom: string }>;
}

interface ReprocessStuckResults {
  totalInProgress: number;
  inScope: number;
  humanAssignedSkipped: number;
  tasksReset: number;
  dryRun: boolean;
  sample: Array<{ id: string; aiCategory: string | null; aiAction: string | null; aiConfidence: number | null; assignedClaimId: string | null; assignedBy: string | null }>;
}

interface ReprocessCancelledResults {
  tasksChecked: number;
  cancelledClaimsFound: number;
  tasksArchived: number;
  errors: number;
  dryRun: boolean;
}

interface FindAiProcessedResults {
  totalCount: number;
  sampleSize: number;
  statusBreakdown: Record<string, number>;
  actionBreakdown: Record<string, number>;
}

interface ResetAiProcessedResults {
  tasksFound: number;
  tasksReset: number;
  dryRun: boolean;
}

/** Preview-then-execute action pair, styled to match the Danger zone. Preview
 * is a dry run (non-mutating); Execute is only enabled once a preview has
 * found something in scope, mirroring the old CAF Backlog section's
 * clawback / reprocess-stuck UX (no window.confirm — the two-step preview
 * gate IS the confirmation). */
const PreviewExecuteGroup: React.FC<{
  title: string;
  description: React.ReactNode;
  previewLabel: string;
  executeLabel: string;
  busy: boolean;
  executeDisabled: boolean;
  onPreview: () => void;
  onExecute: () => void;
  children?: React.ReactNode;
}> = ({ title, description, previewLabel, executeLabel, busy, executeDisabled, onPreview, onExecute, children }) => (
  <div className="pc-danger-group">
    <h4>{title}</h4>
    <p className="pc-desc">{description}</p>
    <div className="pc-actions">
      <Button size="sm" variant="secondary" disabled={busy} onClick={onPreview}>{previewLabel}</Button>
      <Button size="sm" variant="danger" disabled={busy || executeDisabled} onClick={onExecute}>{executeLabel}</Button>
    </div>
    {children}
  </div>
);

export const CafBacklogPanel: React.FC = () => {
  const { apiRequest } = useAuth();
  const queryClient = useQueryClient();
  const states = useProcessorStates();
  const aiSwitch = useAiSwitch(true);
  const moved = switchMoved(aiSwitch);

  const [batchSize, setBatchSize] = useState(100);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [haikuBusy, setHaikuBusy] = useState(false);
  const [engine, setEngine] = useState('haiku');
  const [draftConfig, setDraftConfig] = useState<CafProviderConfig | null>(null);
  const [configBusy, setConfigBusy] = useState(false);
  const [configConflict, setConfigConflict] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const configQuery = useQuery({
    queryKey: ['caf-provider-config', engine],
    queryFn: () => apiRequest<{config:CafProviderConfig; models:Record<string,string[]>; workers:{active_count:number}}>({url:CONFIG_URL+'?engine='+engine, method:'GET'}),
    refetchInterval: 10000,
  });
  useEffect(() => {
    if (configQuery.data?.config && !draftConfig) setDraftConfig({...configQuery.data.config});
  }, [configQuery.data, draftConfig]);
  const saveConfig = async () => {
    if (!draftConfig) return;
    setConfigBusy(true);
    setConfigError(null);
    try {
      const result = await apiRequest<{config:CafProviderConfig}>({url:CONFIG_URL+'?engine='+engine,method:'PUT',data:draftConfig});
      setDraftConfig(result.config);
      setMessage('CAF configuration saved. Future acquisitions use this version; activation is unchanged.');
      configQuery.refetch();
    } catch (err: any) {
      setConfigConflict(err?.response?.status === 409);
      setConfigError(err?.response?.data?.error || 'Failed to save CAF configuration');
    } finally { setConfigBusy(false); }
  };
  const reloadConfig = async () => {
    const result = await configQuery.refetch();
    if (result.data?.config) {
      setDraftConfig({...result.data.config}); setConfigConflict(false); setConfigError(null);
    }
  };

  const [clawbackBusy, setClawbackBusy] = useState(false);
  const [clawbackResults, setClawbackResults] = useState<ClawbackResults | null>(null);
  const [reprocessStuckBusy, setReprocessStuckBusy] = useState(false);
  const [reprocessStuckResults, setReprocessStuckResults] = useState<ReprocessStuckResults | null>(null);

  // Approved 2026-07-14: reprocess-cancelled and reset-ai-processed are
  // preview (dryRun:true) THEN confirmed-execute (dryRun:false); Execute
  // stays disabled until a preview exists, and the preview is cleared once
  // an execute completes so a fresh preview is required before the next one.
  const [reprocessCancelledBusy, setReprocessCancelledBusy] = useState(false);
  const [reprocessCancelledPreview, setReprocessCancelledPreview] = useState<ReprocessCancelledResults | null>(null);
  const [findAiBusy, setFindAiBusy] = useState(false);
  const [findAiResults, setFindAiResults] = useState<FindAiProcessedResults | null>(null);
  const [resetAiBusy, setResetAiBusy] = useState(false);
  const [resetAiPreview, setResetAiPreview] = useState<ResetAiProcessedResults | null>(null);

  const statusQuery = useQuery({
    queryKey: ['caf-backlog-status'],
    queryFn: () => apiRequest<any>({ url: STATUS_URL, method: 'GET' }),
  });
  const status = statusQuery.data;

  // Batch size is synced FROM the server (as in the old panel) whenever the
  // status refreshes, not just user input.
  useEffect(() => {
    if (status?.batchSize != null) setBatchSize(status.batchSize);
  }, [status?.batchSize]);

  const haikuQuery = useQuery({
    queryKey: ['caf-backlog-haiku-status'],
    queryFn: () => apiRequest<{ success: boolean; status: any }>({ url: HAIKU_STATUS_URL, method: 'GET' }),
    refetchInterval: (query) => (query.state.data?.status?.running ? 10000 : false),
  });
  const haiku = haikuQuery.data?.status;

  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['caf-backlog-status'] });
  const refreshHaiku = () => queryClient.invalidateQueries({ queryKey: ['caf-backlog-haiku-status'] });

  const toggleProcessor = async (enable: boolean) => {
    setBusy(true);
    try {
      await apiRequest({ url: `/ui/admin/processors/caf-backlog/${enable ? 'enable' : 'disable'}`, method: 'POST' });
      setMessage(`CAF Backlog Processor ${enable ? 'enabled' : 'disabled'} successfully`);
      refreshStatus();
    } catch {
      setMessage(`Failed to ${enable ? 'enable' : 'disable'} processor`);
    } finally {
      setBusy(false);
    }
  };

  const runCafBacklogProcessor = async (customBatchSize?: number, processAllExisting?: boolean) => {
    setBusy(true);
    setMessage(processAllExisting ? 'Processing entire CAF backlog...' : 'Running CAF backlog processor...');
    try {
      const payload: Record<string, unknown> = { batchSize: customBatchSize ?? batchSize };
      if (processAllExisting) payload.processAll = true;
      const res = await apiRequest<{ success: boolean; message?: string; stats?: any; results?: any }>({
        url: '/ui/admin/processors/caf-backlog/run', method: 'POST', data: payload,
      });
      const stats = res.stats || res.results;
      if (res.success && stats) {
        let summary = `Processing completed: ${stats.totalProcessed} tasks processed, ${stats.totalSuccess} successful, ${stats.totalErrors} errors`;
        if (processAllExisting && typeof stats.initialPending === 'number') {
          summary = `Processed ${stats.totalProcessed} of ${stats.initialPending} pending tasks (${stats.totalErrors} errors)`;
        }
        setMessage(summary);
        refreshStatus();
      } else {
        setMessage(res.message || 'Failed to run processor');
      }
    } catch {
      setMessage('Failed to run CAF backlog processor');
    } finally {
      setBusy(false);
    }
  };

  const startHaikuBackfill = async () => {
    if (!window.confirm(HAIKU_CONFIRM_TEXT)) return;
    setHaikuBusy(true);
    try {
      const res = await apiRequest<{ success: boolean; message?: string; error?: string }>({
        url: '/ui/admin/processors/caf-backlog/haiku-backfill/start', method: 'POST', data: {},
      });
      setMessage(res.success ? 'Configured backfill started' : (res.error || 'Failed to start backfill'));
      refreshHaiku();
    } catch (err: any) {
      setMessage(err?.response?.data?.error || 'Failed to start Haiku backfill');
    } finally {
      setHaikuBusy(false);
    }
  };

  const stopHaikuBackfill = async () => {
    try {
      await apiRequest({ url: '/ui/admin/processors/caf-backlog/haiku-backfill/stop', method: 'POST', data: {} });
      setMessage('Haiku backfill stop requested');
      refreshHaiku();
    } catch {
      setMessage('Failed to request backfill stop; reload shared status and retry.');
    }
  };

  const clawbackDuplicateClaims = async (dryRun: boolean) => {
    setClawbackBusy(true);
    setMessage(dryRun ? 'Scanning for duplicate claim emails...' : 'Clawing back duplicate claim emails...');
    try {
      const res = await apiRequest<{ success: boolean; message?: string; results: ClawbackResults }>({
        url: '/ui/admin/processors/caf-backlog/clawback-duplicate-claims', method: 'POST', data: { dryRun, batchSize: 500 },
      });
      if (res.success) {
        setClawbackResults(res.results);
        setMessage(`${dryRun ? 'Found' : 'Reset'} ${res.results.duplicateClaimsFound} duplicate claim emails (scanned ${res.results.tasksScanned})`);
      } else {
        setMessage(res.message || 'Clawback failed');
      }
    } catch {
      setMessage('Failed to run duplicate claims clawback');
    } finally {
      setClawbackBusy(false);
    }
  };

  const reprocessStuckTasks = async (dryRun: boolean) => {
    setReprocessStuckBusy(true);
    setMessage(dryRun ? 'Counting stuck CAF tasks…' : 'Resetting stuck CAF tasks…');
    try {
      const res = await apiRequest<{ success: boolean; message?: string; results: ReprocessStuckResults }>({
        url: '/ui/admin/processors/caf-backlog/reprocess-stuck', method: 'POST', data: { dryRun, preserveHumanAssigned: true },
      });
      if (res.success) {
        setReprocessStuckResults(res.results);
        const verb = dryRun ? 'Would reset' : 'Reset';
        setMessage(`${verb} ${res.results.inScope} stuck tasks (${res.results.humanAssignedSkipped} human-assigned preserved, ${res.results.totalInProgress} total in_progress)`);
      } else {
        setMessage(res.message || 'Reprocess failed');
      }
    } catch {
      setMessage('Failed to reprocess stuck CAF tasks');
    } finally {
      setReprocessStuckBusy(false);
    }
  };

  const reprocessCancelledClaimTasks = async (dryRun: boolean) => {
    if (!dryRun) {
      const count = reprocessCancelledPreview?.cancelledClaimsFound ?? 0;
      if (!window.confirm(
        `Archive ${count} tasks assigned to cancelled claims? This cannot be undone.`,
      )) return;
    }
    setReprocessCancelledBusy(true);
    setMessage(dryRun ? 'Checking tasks assigned to cancelled claims…' : 'Archiving tasks assigned to cancelled claims…');
    try {
      const res = await apiRequest<{ success: boolean; message?: string; results: ReprocessCancelledResults }>({
        url: '/ui/admin/processors/caf-backlog/reprocess-cancelled', method: 'POST', data: { dryRun, batchSize: 100 },
      });
      if (res.success) {
        setReprocessCancelledPreview(dryRun ? res.results : null);
        setMessage(res.message || null);
      } else {
        setMessage(res.message || 'Reprocess cancelled-claims failed');
      }
    } catch {
      setMessage('Failed to reprocess cancelled-claim tasks');
    } finally {
      setReprocessCancelledBusy(false);
    }
  };

  const findAiProcessedTasks = async () => {
    setFindAiBusy(true);
    setMessage('Finding AI-processed tasks…');
    try {
      const res = await apiRequest<{ success: boolean; message?: string; results: FindAiProcessedResults }>({
        url: '/ui/admin/processors/caf-backlog/find-ai-processed', method: 'POST', data: { batchSize: 500 },
      });
      if (res.success) {
        setFindAiResults(res.results);
        setMessage(res.message || null);
      } else {
        setMessage(res.message || 'Find AI-processed failed');
      }
    } catch {
      setMessage('Failed to find AI-processed tasks');
    } finally {
      setFindAiBusy(false);
    }
  };

  const resetAiProcessedTasks = async (dryRun: boolean) => {
    if (!dryRun) {
      const count = resetAiPreview?.tasksFound ?? 0;
      if (!window.confirm(
        `Reset ${count} tasks to pending and wipe their AI classification? They will all be reprocessed.`,
      )) return;
    }
    setResetAiBusy(true);
    setMessage(dryRun ? 'Checking AI-processed tasks…' : 'Resetting AI-processed tasks…');
    try {
      const res = await apiRequest<{ success: boolean; message?: string; results: ResetAiProcessedResults }>({
        url: '/ui/admin/processors/caf-backlog/reset-ai-processed', method: 'POST', data: { dryRun },
      });
      if (res.success) {
        setResetAiPreview(dryRun ? res.results : null);
        setMessage(res.message || null);
      } else {
        setMessage(res.message || 'Reset AI-processed failed');
      }
    } catch {
      setMessage('Failed to reset AI-processed tasks');
    } finally {
      setResetAiBusy(false);
    }
  };

  const lastRun = status?.lastRun?.results;

  return (
    <Card title="CAF Backlog" actions={moved ? undefined : (
      <>
        <Button size="sm" variant="secondary" disabled={status?.enabled || busy} onClick={() => toggleProcessor(true)}>Enable Processor</Button>
        <Button size="sm" variant="ghost" disabled={!status?.enabled || busy} onClick={() => toggleProcessor(false)}>Disable Processor</Button>
      </>
    )}>
      <p className="pc-desc">
        Processes pending CAF inbox email tasks using AI classification. Automatically categorizes emails,
        extracts claim information, matches claims, updates key dates, and takes appropriate actions (archive, assign, or manual review).
      </p>

      {moved && aiSwitch.value && <AutomationSwitchNotice mode={aiSwitch.value.mode} />}
      <ProcessorStatePanel processorId="caf_backlog" state={states.caf_backlog} readOnly={moved} />

      <fieldset className="pc-config" disabled={configBusy}>
        <legend>CAF provider and global capacity</legend>
        <label>Processor lane
          <select aria-label="CAF processor lane" value={engine} onChange={e => {setEngine(e.target.value); setDraftConfig(null); setConfigConflict(false); setConfigError(null);}}>
            <option value="haiku">Backfill (Haiku lane)</option>
            <option value="palantir">Scheduled (Palantir lane)</option>
          </select>
        </label>
        <label>Provider
          <select aria-label="CAF provider" value={draftConfig?.provider || ''} disabled={!draftConfig} onChange={e => {
            const provider=e.target.value;
            if (draftConfig) setDraftConfig({...draftConfig,provider,model:configQuery.data?.models[provider]?.[0] || ''});
          }}>
            {!draftConfig && <option value="">Loading…</option>}
            {Object.keys(configQuery.data?.models || {}).map(provider => <option key={provider} value={provider}>{provider}</option>)}
          </select>
        </label>
        <label>Exact model
          <select aria-label="CAF model" value={draftConfig?.model || ''} disabled={!draftConfig} onChange={e => draftConfig && setDraftConfig({...draftConfig,model:e.target.value})}>
            {(configQuery.data?.models[draftConfig?.provider || ''] || []).map(model => <option key={model} value={model}>{model}</option>)}
          </select>
        </label>
        <label>Global concurrent tasks
          <input aria-label="Global concurrent tasks" type="number" min={1} max={8} step={1} value={draftConfig?.global_concurrency ?? ''}
            onChange={e => draftConfig && setDraftConfig({...draftConfig,global_concurrency:Number(e.target.value)})} />
        </label>
        <label>Model retries
          <input aria-label="Model retries" type="number" min={0} max={5} step={1} value={draftConfig?.max_retries ?? ''}
            onChange={e => draftConfig && setDraftConfig({...draftConfig,max_retries:Number(e.target.value)})} />
        </label>
        <Button disabled={!draftConfig || configQuery.isError || configConflict || !Number.isInteger(draftConfig.global_concurrency) || draftConfig.global_concurrency < 1 || draftConfig.global_concurrency > 8 || !Number.isInteger(draftConfig.max_retries) || draftConfig.max_retries < 0 || draftConfig.max_retries > 5} onClick={saveConfig}>Save CAF configuration</Button>
        <Button variant="secondary" onClick={reloadConfig}>Reload CAF configuration</Button>
        <p className="pc-desc">Version {draftConfig?.config_version ?? 'unknown'} · Active tasks across all instances and engines: {configQuery.isError ? 'unknown' : configQuery.data?.workers?.active_count ?? 'unknown'}. Lowering capacity lets active tasks finish.</p>
        <p className="pc-desc">Saving affects future acquisitions for the selected lane and does not start processors. Model retries do not authorize another email send. Check the AI provider's spending limits, and review answer quality separately.</p>
        {(configError || configQuery.isError) && <p role="alert">{configError || 'Shared CAF configuration unavailable'}</p>}
      </fieldset>

      {status && (
        <dl className="pc-status">
          <dt>Waiting emails</dt><dd>{status.pendingTasks}</dd>
          <dt>Batch size</dt><dd>{status.batchSize}</dd>
          {status.nextRun && (<><dt>Next run</dt><dd>{formatWhen(status.nextRun)}</dd></>)}
          {status.lastRun?.time && (<><dt>Last run</dt><dd>{formatWhen(status.lastRun.time)}</dd></>)}
        </dl>
      )}

      {lastRun && (
        <div className="pc-stat-grid">
          <StatCard value={lastRun.totalProcessed} label="processed" />
          <StatCard value={lastRun.totalSuccess} label="successful" tone="success" />
          <StatCard value={lastRun.totalErrors} label="errors" tone={lastRun.totalErrors > 0 ? 'error' : 'muted'} />
          <StatCard value={lastRun.claimsMatched} label="claims matched" />
          <StatCard value={lastRun.tasksArchived} label="tasks archived" />
          <StatCard value={lastRun.tasksAssigned} label="tasks assigned" />
          <StatCard value={lastRun.tasksCompleted} label="tasks completed" />
          <StatCard value={lastRun.keyDatesUpdated} label="key dates updated" />
          <StatCard value={(lastRun.totalTokens ?? 0).toLocaleString()} label="tokens used" />
          <StatCard value="Unknown" label="cost" />
        </div>
      )}

      {!moved && <div className="pc-config">
        <label>Batch Size
          <input type="number" min={1} max={500} aria-label="Batch size" value={batchSize}
                 disabled={status?.enabled}
                 onChange={(e) => setBatchSize(parseInt(e.target.value, 10) || 100)} />
        </label>
      </div>}

      <div className="pc-actions">
        {!moved && <>
        <Button variant="primary" disabled={busy} onClick={() => runCafBacklogProcessor()}>{busy ? 'Running...' : 'Run Now'}</Button>
        <Button variant="secondary" disabled={busy} onClick={() => runCafBacklogProcessor(10)}>Test Run (10 tasks)</Button>
        <Button variant="secondary" disabled={busy} onClick={() => runCafBacklogProcessor(1)}>Single Task Test</Button>
        <Button variant="danger" disabled={busy} onClick={() => runCafBacklogProcessor(undefined, true)}>Process Entire Backlog</Button>
        <Button variant="secondary" disabled={haikuBusy || haiku?.running || configQuery.isError || !configQuery.data} onClick={startHaikuBackfill}>
          {haiku?.running ? 'Backfill Running…' : 'Run configured backfill'}
        </Button>
        {haiku?.running && (
          <Button variant="ghost" onClick={stopHaikuBackfill}>Stop backfill</Button>
        )}
        </>}
        <Button variant="secondary" disabled={busy} onClick={refreshStatus}>Refresh Status</Button>
      </div>

      {haiku && (haiku.running || haiku.processed > 0) && (
        <div className="pc-haiku-panel">
          <strong>Backfill (Haiku lane)</strong>{' '}
          {haiku.running ? '— running' : '— finished'}
          {haiku.stopRequested && haiku.running ? ' (stopping…)' : ''}
          <div style={{ margin: '8px 0' }}>
            <ProgressBar value={haiku.processed} max={haiku.totalTasks} />
          </div>
          <p className="pc-desc">
            {haiku.processed}/{haiku.totalTasks} processed · {haiku.remaining} remaining
          </p>
          <div className="pc-stat-grid">
            <StatCard value={haiku.processed} label="processed" />
            <StatCard value={haiku.remaining} label="remaining" />
            <StatCard value={haiku.success} label="ok" tone="success" />
            <StatCard value={haiku.errors} label="errors" tone={haiku.errors > 0 ? 'error' : 'muted'} />
            <StatCard value={haiku.skipped} label="skipped" tone="muted" />
            <StatCard value={(haiku.usage?.inputTokens ?? 0) + (haiku.usage?.outputTokens ?? 0)} label="tokens" />
            <StatCard value={haiku.estimatedCostUsd == null ? 'Unknown' : `$${haiku.estimatedCostUsd.toFixed(2)}`} label="est. cost" />
            <StatCard value={haiku.llmCalls ?? 0} label="provider requests" />
          </div>
          {haiku.lastError && <p className="pc-desc" style={{ color: 'var(--color-error-700)' }}>Last error: {haiku.lastError}</p>}
        </div>
      )}

      {message && <p className="pc-desc">{message}</p>}

      <DangerZone>
        <PreviewExecuteGroup
          title="Clawback Duplicate Claim Emails"
          description="Find and reset completed emails that should have been flagged as duplicate claims (lender said complaint already exists and did NOT log a new one)."
          previewLabel={clawbackBusy ? 'Scanning...' : 'Preview Clawback'}
          executeLabel="Execute Clawback"
          busy={clawbackBusy || busy}
          executeDisabled={!clawbackResults?.duplicateClaimsFound}
          onPreview={() => clawbackDuplicateClaims(true)}
          onExecute={() => clawbackDuplicateClaims(false)}
        >
          {clawbackResults && (
            <p className="pc-desc">
              <strong>{clawbackResults.dryRun ? 'Preview:' : 'Completed:'}</strong>{' '}
              {clawbackResults.duplicateClaimsFound} duplicate claim emails found
              {!clawbackResults.dryRun && ` (${clawbackResults.tasksReset} reset)`}
            </p>
          )}
        </PreviewExecuteGroup>

        <PreviewExecuteGroup
          title="Reprocess Stuck Tasks (Re-classify with Current Rules)"
          description="Reset AI-classified CAF tasks still sitting in manual review so the backlog processor re-runs them with the current prompt + rules. Human-assigned tasks are skipped and preserved."
          previewLabel={reprocessStuckBusy ? 'Counting…' : 'Preview Reprocess'}
          executeLabel="Execute Reprocess"
          busy={reprocessStuckBusy || busy}
          executeDisabled={!reprocessStuckResults?.inScope}
          onPreview={() => reprocessStuckTasks(true)}
          onExecute={() => reprocessStuckTasks(false)}
        >
          {reprocessStuckResults && (
            <p className="pc-desc">
              <strong>{reprocessStuckResults.dryRun ? 'Preview:' : 'Completed:'}</strong>{' '}
              <strong>{reprocessStuckResults.inScope}</strong> tasks {reprocessStuckResults.dryRun ? 'would be' : 'were'} reset to pending
              {!reprocessStuckResults.dryRun && ` (${reprocessStuckResults.tasksReset} actually updated)`}.{' '}
              Human-assigned skipped: {reprocessStuckResults.humanAssignedSkipped}.
            </p>
          )}
        </PreviewExecuteGroup>

        {/* Ryan approved exposing these three previously frontend-orphaned routes on
            2026-07-14, on condition each destructive one requires a preview (dry run)
            before an Execute button that's disabled until that preview exists AND
            window.confirm's the previewed count + consequence. */}
        <PreviewExecuteGroup
          title="Reprocess Cancelled-Claim Tasks"
          description="Reset stuck CAF tasks that are assigned to a claim which has since been cancelled, so they get archived instead of sitting in review."
          previewLabel={reprocessCancelledBusy ? 'Checking…' : 'Dry Run Cancelled-Claim Archive'}
          executeLabel="Execute Cancelled-Claim Archive"
          busy={reprocessCancelledBusy || busy}
          executeDisabled={!reprocessCancelledPreview?.cancelledClaimsFound}
          onPreview={() => reprocessCancelledClaimTasks(true)}
          onExecute={() => reprocessCancelledClaimTasks(false)}
        >
          {reprocessCancelledPreview && (
            <p className="pc-desc">
              Checked {reprocessCancelledPreview.tasksChecked} tasks — {reprocessCancelledPreview.cancelledClaimsFound} matched a cancelled claim and will be archived.
            </p>
          )}
        </PreviewExecuteGroup>

        <div className="pc-danger-group">
          <h4>Find AI-Processed Tasks</h4>
          <p className="pc-desc">Look for AI-processed tasks still stuck in_progress and break them down by status/action, without changing anything.</p>
          <div className="pc-actions">
            <Button size="sm" variant="secondary" disabled={findAiBusy} onClick={findAiProcessedTasks}>
              {findAiBusy ? 'Finding…' : 'Find AI-Processed Tasks'}
            </Button>
          </div>
          {findAiResults && (
            <p className="pc-desc">
              Found {findAiResults.totalCount} AI-processed tasks (sample {findAiResults.sampleSize}).
            </p>
          )}
        </div>

        <PreviewExecuteGroup
          title="Reset AI-Processed Tasks"
          description="Reset AI-processed tasks stuck in_progress back to pending, wiping all AI fields AND assignment — a stronger reset than 'Reprocess Stuck Tasks' above."
          previewLabel={resetAiBusy ? 'Checking…' : 'Preview Reset'}
          executeLabel="Execute Reset"
          busy={resetAiBusy || busy}
          executeDisabled={!resetAiPreview?.tasksFound}
          onPreview={() => resetAiProcessedTasks(true)}
          onExecute={() => resetAiProcessedTasks(false)}
        >
          {resetAiPreview && (
            <p className="pc-desc">
              Preview: {resetAiPreview.tasksFound} tasks found and will be reset to pending.
            </p>
          )}
        </PreviewExecuteGroup>
      </DangerZone>
    </Card>
  );
};
